/* eslint-disable react/jsx-props-no-spreading */
import {Trans} from '@lingui/macro';
import React, {FC, Key} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../../../libs/shared/helpers/src/types/customErrors';
import {InlineLinkButton} from '../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {
  TaggedVisualBasicActivity,
  TopicAddedVisualBasicActivity,
  TopicClosedVisualBasicActivity,
  TopicRemovedVisualBasicActivity,
  UnTaggedVisualBasicActivity,
  VisualBasicActivity,
  VisualBasicActivityTypesEnum,
} from '../../../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicActivity';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {
  ActivityTopicTypesEnum,
  BasicActivityModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
} from '../../../../../models/api/activityModel';
import {
  ActivitySourceContactModel,
  ActivitySourceModel,
  ActivitySourceOtherModel,
  ActivitySourceRuleModel,
  ActivitySourceTeammateModel,
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../../../models/api/activitySourceModel';
import {ConversationActionContextsEnum} from '../../../../../models/api/conversationCommonTypes';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {IntegrationModel} from '../../../../../models/api/integrationModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {TopicModel} from '../../../../../models/api/topicModel';
import {buildPublicId} from '../../../../../models/applications/publicIdTypes';
import {
  InterfaceEventsEnum,
  TimelineActivityActionsEnum,
  trackInterfaceEvent,
} from '../../../../../services/tracking';
import {BasicViewerGroupHeaderContainerProps} from '../../../../../store/data/activities/activitiesCollapseSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {helpCenterUrl} from '../../../../../store/stateless/navigationActions';
import {useLinkNavigation} from '../../../../common/navigation/useLinkNavigation';
import {ActivityRulePillContainer} from '../activityRulePill/activityRulePillContainer';
import {ActivityTagPillContainer} from '../activityTagPill/activityTagPillContainer';
import {ActivityTopicPillContainer} from '../activityTopicPill/activityTopicPillContainer';
import {ActivityWithIcon} from '../activityWithIcon/activityWithIcon';
import {BasicActivityErrorViewerContainer} from './basicActivityErrorViewerContainer';
import {BasicGroupHeaderContainer} from './basicGroupHeaderContainer';
import {BasicViewerBase} from './basicViewerBase';
import {BasicViewerResnoozeContainer} from './basicViewerResnoozeContainer';

/*
 * Constants.
 */

const helpCenterDataLeakUrl = `${helpCenterUrl}/t/m2bx52/how-smart-merge-works#why-did-my-smart-merged-conversation-split-into-a-new-conversation`;

/*
 * Props.
 */

interface BasicViewerProps extends BasicViewerGroupHeaderContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  user: TeammateModel;
  userId: number;
  activity: BasicActivityModel;
  activityIndex: number;
  focusKey: Key | undefined;
  isPreviousActivityBasic: boolean;
  sourceTeammate?: TeammateModel;
  visualBasicActivity: VisualBasicActivity | undefined;
  namespace: string;
  navigateToLink: HandlerOf<string>;
  isPersonalizedDemoInboxActive?: boolean;
  isFirstOfGroup: boolean;
  isExpanded: boolean;
  topicIntegration?: IntegrationModel;
  conversationId: number;
  canceledReminder?: ReminderModel;
  conversationActionContext: ConversationActionContextsEnum | undefined;
}

interface TeammateActivitySourceProps {
  userId: number;
  sourceTeammate: TeammateModel;
}

interface OutOfOfficeActivitySourceProps {
  userId: number;
  sourceTeammate: TeammateModel;
}

interface RuleActivitySourceProps {
  activitySource: ActivitySourceRuleModel;
  timelineActivityAction: TimelineActivityActionsEnum;
}

interface ApiActivitySourceProps {
  activitySource: ActivitySourceModel;
}

/*
 * Style.
 */

const StyledCapitalizeSpan = styled.span`
  text-transform: capitalize;
`;

const StyledRulePillWrapperDiv = styled.div`
  margin-left: 2px;
  display: inline-block;
`;

/*
 * Component.
 */

export const BasicViewer: FC<BasicViewerProps> = (props) => {
  // Whether this activity is the one corresponding to the tag application by a rule in the
  // personalized demo inbox.
  const isPersonalizedDemoInboxRuleActivity =
    props.isPersonalizedDemoInboxActive &&
    props.activity.basicVisualType === BasicVisualTypesEnum.TAG &&
    props.activity.source.type === ActivitySourceTypesEnum.RULE;

  return (
    <>
      {props.isFirstOfGroup && (
        <BasicGroupHeaderContainer
          activityIndex={props.activityIndex}
          focusKey={props.focusKey}
          activityId={props.activity.id}
          conversationId={props.activity.conversationId}
          panelId={props.panelId}
          activities={props.activities}
          isExpanded={props.isExpanded}
        />
      )}
      {props.isExpanded && (
        <>
          <BasicViewerBase
            isPreviousActivityBasic={props.isFirstOfGroup || props.isPreviousActivityBasic}
            date={props.activity.updatedAt}
            isPersonalizedDemoInboxRuleActivity={isPersonalizedDemoInboxRuleActivity}
          >
            {renderVisualActivity(props)}
          </BasicViewerBase>
          {maybeRenderResnoozeNotification(props)}
        </>
      )}
    </>
  );
};

/*
 * Content.
 */

function maybeRenderResnoozeNotification(props: BasicViewerProps) {
  const {canceledReminder, cellsContext, conversationId, conversationActionContext} = props;
  if (!conversationActionContext || !canceledReminder) {
    return null;
  }

  return (
    <BasicViewerResnoozeContainer
      canceledReminder={canceledReminder}
      cellsContext={cellsContext}
      conversationId={conversationId}
      conversationActionContext={conversationActionContext}
    />
  );
}

function renderVisualActivity(props: BasicViewerProps) {
  const {userId, visualBasicActivity: visualActivity} = props;
  if (!visualActivity) {
    return null;
  }

  switch (visualActivity.type) {
    case VisualBasicActivityTypesEnum.ASSIGNED_USER_THEMSELVES:
      return (
        <ActivityWithIcon iconName="assignActivity">
          <Trans id="activity-basic-assign-you-self">
            <strong>Assigned</strong> to yourself
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_TEAMMATE_THEMSELVES:
      return (
        <ActivityWithIcon iconName="assignActivity">
          <Trans id="activity-basic-assign-teammate-self">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
            themselves
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="assignActivity">
          <Trans id="activity-basic-assign-teammate">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_BY_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-assign-rule">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
          </Trans>{' '}
          <RuleActivitySource
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_ASSIGNMENT}
          />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_BY_API:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-assign-api">
            <strong>Assigned</strong> to {renderTeammateNameOrYou(userId, visualActivity.assignee)} by
          </Trans>{' '}
          <ApiActivitySource activitySource={visualActivity.source} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ASSIGNED_OTHER:
      return (
        <Trans id="activity-basic-old-assign">
          This conversation was assigned to {buildTeammateDisplayName(visualActivity.assignee)}{' '}
          <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.MOVED_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="moveActivity">
          <Trans id="activity-basic-move-teammate">
            <strong>Moved</strong> to {visualActivity.inbox.name} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.MOVED_BY_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-move-rule">
            <strong>Moved to {visualActivity.inbox.name}</strong> by
          </Trans>{' '}
          <RuleActivitySource
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_MOVE}
          />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.MOVED_BY_OTHER:
      return (
        <Trans id="activity-basic-old-move">
          This conversation was moved to {visualActivity.inbox.name} <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.TAG:
      return renderTagActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.UNTAG:
      return renderUnTagActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.TOPIC_CLOSED:
      return renderTopicClosedActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.TOPIC_ADDED:
      return renderTopicAddedActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.TOPIC_REMOVED:
      return renderTopicRemovedActivity(props, visualActivity);

    case VisualBasicActivityTypesEnum.ERROR:
      return (
        <BasicActivityErrorViewerContainer
          activityError={visualActivity.activityError}
          activitySource={visualActivity.activitySource}
          namespace={props.namespace}
        />
      );

    case VisualBasicActivityTypesEnum.FORWARDED_BY_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-forward-rule">
            <strong>Forwarded</strong> to {visualActivity.contactHandle} by
          </Trans>{' '}
          <RuleActivitySource
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_FORWARD}
          />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.FORWARDED_BY_OTHER:
      return (
        <Trans id="activity-basic-old-forward">
          This conversation was forwarded to {visualActivity.contactHandle} <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_USER_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inviteActivity">
          <Trans id="activity-tracker-share-teammate-to-you">
            <strong>You were invited</strong> by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inviteActivity">
          <Trans id="activity-tracker-share-teammate-to-teammate">
            <strong>
              <StyledCapitalizeSpan>
                {renderTeammateNameOrYou(userId, visualActivity.recipient)}
              </StyledCapitalizeSpan>{' '}
              was invited
            </strong>{' '}
            by <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-tracker-share-rule">
            <strong>Notified</strong> {renderTeammateNameOrYou(userId, visualActivity.recipient)} by
          </Trans>{' '}
          <RuleActivitySource
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_NOTIFY_TEAMMATE}
          />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_DELETED_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-tracker-share-rule">
            <strong>Notified</strong> {renderTeammateNameOrYou(userId, visualActivity.recipient)} by
          </Trans>{' '}
          a deleted rule.
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_SHARE_OTHER:
      return (
        <Trans id="activity-tracker-old-share">
          {buildTeammateDisplayName(visualActivity.recipient)} was added to the conversation{' '}
          <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.TRACKER_UNSHARE_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="removeActivity">
          <Trans id="activity-tracker-unshare-by-teammate">
            <strong>
              <StyledCapitalizeSpan>
                {renderTeammateNameOrYou(userId, visualActivity.recipient)}
              </StyledCapitalizeSpan>{' '}
              was removed
            </strong>{' '}
            by <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRACKER_UNSHARE_USER_BY_TEAMMATE:
      return (
        <ActivityWithIcon iconName="removeActivity">
          <Trans id="activity-tracker-unshare-user-by-teammate">
            <strong>You were removed</strong> by{' '}
            <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_INACTIVITY:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-inactivity">
            <strong>Automatically unassigned</strong> due to inactivity
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_USER:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-teammate-you">
            <strong>Unassigned</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-teammate">
            <strong>Unassigned</strong> by{' '}
            <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_OOO:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-ooo">
            <strong>Automatically unassigned</strong>
          </Trans>{' '}
          <OutOfOfficeActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_DELETED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="unassignActivity">
          <Trans id="activity-basic-unassign-deleted">
            <strong>Automatically unassigned</strong> because the teammate is no longer available
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.UNASSIGNED_OTHER:
      return (
        <Trans id="activity-basic-old-unassign">
          This conversation was unassigned <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_TEAMMATE:
      return (
        <ActivityWithIcon iconName="snoozeActivity">
          <Trans id="activity-basic-archive-snooze">
            <strong>Snoozed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_RULE:
      return (
        <ActivityWithIcon iconName="snoozeActivity">
          <Trans id="activity-basic-archive-snooze-rule">
            <strong>Snoozed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <RuleActivitySource
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_SNOOZE}
          />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_OTHER:
      return (
        <Trans id="activity-basic-old-archive-snooze">
          This conversation was snoozed <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="archiveActivity">
          <Trans id="activity-basic-archive-teammate">
            <strong>Archived</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-archive-rule">
            <strong>Archived</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <RuleActivitySource
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_ARCHIVE}
          />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.ARCHIVED_OTHER:
      return (
        <Trans id="activity-basic-old-archive">
          This conversation was archived <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.REOPEN_USER:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-reoopen-you">
            <strong>Reopened</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-reoopen-teammate">
            <strong>Reopened</strong> by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-reoopen-rule">
            <strong>Reopened</strong> by a rule
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_MESSAGE_SENT:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-reoopen-sending">
            <strong>Reopened</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.REOPEN_OTHER:
      return (
        <Trans id="activity-basic-old-reopen">
          This conversation was reopened <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.TRASHED_TEAMMATE:
      return (
        <ActivityWithIcon iconName="trashActivity">
          <Trans id="activity-basic-trash">
            <strong>Trashed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRASHED_SPAM:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-trash-spamfilter">
            <strong>Trashed</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by spam
            filtering
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRASHED_DATA_RETENTION:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-trash-dataretention">
            <strong>Trashed</strong> by data retention policy
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.TRASHED_OTHER:
      return (
        <Trans id="activity-basic-old-trash">
          This conversation was trashed <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.MERGE_TEAMMATE:
      return (
        <ActivityWithIcon iconName="mergeActivity">
          <Trans id="activity-basic-merge">
            <strong>Conversation merged</strong> with another conversation by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.MERGE_OTHER:
      return (
        <Trans id="activity-basic-old-merge">
          This conversation was merged with another <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.RESTORE_TEAMMATE:
      return (
        <ActivityWithIcon iconName="inboxActivity">
          <Trans id="activity-basic-restore">
            <strong>Restored</strong> in {renderInboxName(visualActivity.originalTeamInboxes)} by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.RESTORE_OTHER:
      return (
        <Trans id="activity-basic-old-restore">
          This conversation was restored <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.RULE_NOTIFIED_EXISTING_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-rulenotify-withrule">
            <strong>Notified</strong> you by
          </Trans>{' '}
          <RuleActivitySource
            activitySource={visualActivity.source}
            timelineActivityAction={TimelineActivityActionsEnum.CLICK_RULE_NOTIFY_YOU}
          />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.RULE_NOTIFIED_DELETED_RULE:
      return (
        <ActivityWithIcon iconName="gearActivity">
          <Trans id="activity-basic-rulenotify-withoutrule">
            A rule <strong>notified</strong> you
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.RULE_NOTIFIED_OTHER:
      return (
        <Trans id="activity-basic-old-rulenotify">
          You were notified <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.REMINDER_EXPIRED:
      return (
        <ActivityWithIcon iconName="snoozeActivity">
          <Trans id="activity-basic-reminder">
            <strong>Snooze expired</strong>
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.SPLIT_ORIGIN_TEAMMATE:
      return (
        <ActivityWithIcon iconName="splitActivity">
          <Trans id="activity-basic-split-origin">
            <strong>Message moved</strong> to{' '}
            <ActivityConversationLink {...props}>new conversation</ActivityConversationLink> by
          </Trans>{' '}
          <TeammateActivitySource userId={userId} sourceTeammate={visualActivity.sourceTeammate} />
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.SPLIT_ORIGIN_DATA_LEAK:
      return (
        <ActivityWithIcon iconName="splitActivity">
          <Trans id="activity-basic-split-origin-privacy">
            <strong>Message moved</strong> to a{' '}
            <ActivityConversationLink {...props}>new conversation</ActivityConversationLink> for privacy
            reasons
          </Trans>
        </ActivityWithIcon>
      );

    case VisualBasicActivityTypesEnum.SPLIT_ORIGIN_OTHER:
      return (
        <Trans id="activity-basic-old-split-origin">
          This conversation was split into a{' '}
          <ActivityConversationLink {...props}>new conversation</ActivityConversationLink>{' '}
          <ActivitySource {...props} />
        </Trans>
      );

    case VisualBasicActivityTypesEnum.SPLIT_TARGET:
      return (
        <Trans id="activity-basic-old-split-target">
          This conversation was started from an{' '}
          <ActivityConversationLink {...props}>existing conversation</ActivityConversationLink>{' '}
          <ActivitySource {...props} />
        </Trans>
      );

    default:
      throw new UnreachableError(visualActivity);
  }
}

function renderTagActivity(props: BasicViewerProps, visualActivity: TaggedVisualBasicActivity) {
  const {tag} = visualActivity;

  // Retrieve the tag source (rule, teammate or API).
  const tagSource = renderTagSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);

  // If we have a tag source available, render that source before the tag.
  if (tagSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        {renderTag(props, tag)}{' '}
        <Trans id="activity-basic-tag">
          <strong>added</strong> by {tagSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  // If tag was not from a rule and we cannot determine the tag source, return the old format as a fallback.
  return (
    <Trans id="activity-basic-old-tag">
      Tag &quot;{tag.name}&quot; was added <ActivitySource {...props} />
    </Trans>
  );
}

function renderUnTagActivity(props: BasicViewerProps, visualActivity: UnTaggedVisualBasicActivity) {
  const {tag} = visualActivity;

  // Retrieve the tag source (rule, teammate or API).
  const tagSource = renderTagSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);

  // If we have a tag source available, render that source before the tag.
  if (tagSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        <Trans id="activity-basic-untag">
          {renderTag(props, tag)} <strong>removed</strong> by {tagSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  // If tag was not from a rule and we cannot determine the tag source, return the old format as a fallback.
  return (
    <Trans id="activity-basic-old-untag">
      Tag &quot;{tag.name}&quot; was removed <ActivitySource {...props} />
    </Trans>
  );
}
function renderTopicClosedActivity(props: BasicViewerProps, visualActivity: TopicClosedVisualBasicActivity) {
  const {topicIntegration} = props;
  const {conversationId} = props.activity;
  const {topic} = visualActivity;

  switch (topic.type) {
    // Handle an issue on JIRA being resolved.
    case ActivityTopicTypesEnum.JIRA: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-jira">
                <strong>Resolved</strong> Jira issue
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    // Handle an issue on GitHub being closed.
    case ActivityTopicTypesEnum.GITHUB: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-github">
                <strong>Closed</strong> Github issue
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    // Handle a task on Asana being completed.
    case ActivityTopicTypesEnum.ASANA: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-asana">
                <strong>Completed</strong> Asana task
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    // Handle a task on Trello being completed.
    case ActivityTopicTypesEnum.TRELLO: {
      return (
        <UserThemeConsumer>
          {(theme) => (
            <ActivityWithIcon iconName="checkmarkYesActivity" iconColor={theme.palette.green.shade40}>
              <Trans id="activity-basic-topic-trello">
                <strong>Moved</strong> Trello card to
              </Trans>{' '}
              {renderTopicPillContainer(visualActivity, conversationId, {topicIntegration})}
            </ActivityWithIcon>
          )}
        </UserThemeConsumer>
      );
    }
    default:
      return (
        <Trans id="activity-basic-topic-closed">
          The {renderTopicTypeName(topic, topicIntegration)} issue &quot;
          {topic.name}
          &quot; was closed
        </Trans>
      );
  }
}

function renderTopicAddedActivity(props: BasicViewerProps, visualActivity: TopicAddedVisualBasicActivity) {
  // Retrieve the topic source (rule, teammate or API).
  const topicSource = renderTopicSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);
  const topicPillOptions = {shouldRemoveLeftMargin: true, topicIntegration: props.topicIntegration};
  const {conversationId} = props.activity;

  // If we have a topic source available, render that source with the topic.
  if (topicSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        {renderTopicPillContainer(visualActivity, conversationId, topicPillOptions)}{' '}
        <Trans id="activity-basic-topic-added">
          <strong>added</strong> by {topicSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  const {topic} = visualActivity;

  // If topic was not from a rule and we cannot determine the topic source, return the old format as
  // a fallback (emulating tags).
  return (
    <Trans id="activity-basic-old-topic-added">
      Topic &quot;{topic.name}&quot; was added <ActivitySource {...props} />
    </Trans>
  );
}

function renderTopicRemovedActivity(
  props: BasicViewerProps,
  visualActivity: TopicRemovedVisualBasicActivity,
) {
  // Retrieve the topic source (rule, teammate or API).
  const topicSource = renderTopicSource(props, visualActivity);
  const iconName = computeTagOrTopicActivityIconName(props.activity);
  const topicPillOptions = {shouldRemoveLeftMargin: true, topicIntegration: props.topicIntegration};
  const {conversationId} = props.activity;

  // If we have a topic source available, render that source with the topic.
  if (topicSource) {
    return (
      <ActivityWithIcon iconName={iconName}>
        {renderTopicPillContainer(visualActivity, conversationId, topicPillOptions)}{' '}
        <Trans id="activity-basic-topic-removed">
          <strong>removed</strong> by {topicSource}
        </Trans>
      </ActivityWithIcon>
    );
  }

  const {topic} = visualActivity;

  // If topic was not from a rule and we cannot determine the topic source, return the old format as
  // a fallback (emulating tags).
  return (
    <Trans id="activity-basic-old-topic-removed">
      Topic &quot;{topic.name}&quot; was removed <ActivitySource {...props} />
    </Trans>
  );
}

type TagBasedVisualBasicActivity = TaggedVisualBasicActivity | UnTaggedVisualBasicActivity;
/* Compute whether a tag was created via rule, teammate, or API and render the result. */
function renderTagSource(props: BasicViewerProps, visualActivity: TagBasedVisualBasicActivity) {
  const {activity} = props;
  const {source} = activity;

  // If the tag/untag was by a rule.
  if (
    source.type === ActivitySourceTypesEnum.RULE &&
    source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    const timelineActivityAction =
      activity.type === BasicActivityTypesEnum.TAG
        ? TimelineActivityActionsEnum.CLICK_RULE_TAG
        : TimelineActivityActionsEnum.CLICK_RULE_UNTAG;

    return <RuleActivitySource activitySource={source} timelineActivityAction={timelineActivityAction} />;
  }

  // If the tag/untag was by a teammate.
  if (visualActivity.sourceTeammate) {
    return <TeammateActivitySource userId={props.userId} sourceTeammate={visualActivity.sourceTeammate} />;
  }

  // If the Front API was responsible for the tag/untag.
  if (
    source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return <ApiActivitySource activitySource={source} />;
  }

  return null;
}

/* Compute whether a topic was created via rule, teammate, or API and render the result. */
function renderTopicSource(props: BasicViewerProps, visualActivity: TopicBasedVisualBasicActivity) {
  const {activity} = props;
  const {source} = activity;

  // If the topic added/removed was by a teammate.
  if (visualActivity.sourceTeammate) {
    return <TeammateActivitySource userId={props.userId} sourceTeammate={visualActivity.sourceTeammate} />;
  }

  // TODO: Validate that rule and public api work.
  // If the topic added/removed was by a rule.
  if (
    source.type === ActivitySourceTypesEnum.RULE &&
    source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    const timelineActivityAction =
      activity.type === BasicActivityTypesEnum.TOPIC_ADDED
        ? TimelineActivityActionsEnum.CLICK_RULE_TOPIC_ADDED
        : TimelineActivityActionsEnum.CLICK_RULE_TOPIC_REMOVED;

    return <RuleActivitySource activitySource={source} timelineActivityAction={timelineActivityAction} />;
  }

  // If the Front API was responsible for the topic added/removed.
  if (
    source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return <ApiActivitySource activitySource={source} />;
  }

  return null;
}

interface TopicPillOptions {
  shouldRemoveLeftMargin?: boolean;
  topicIntegration?: IntegrationModel;
}

type TopicBasedVisualBasicActivity =
  | TopicAddedVisualBasicActivity
  | TopicClosedVisualBasicActivity
  | TopicRemovedVisualBasicActivity;

function renderTopicPillContainer(
  activity: TopicBasedVisualBasicActivity,
  conversationId: number,
  options?: TopicPillOptions,
) {
  const {topic} = activity;

  switch (topic.type) {
    // TODO: support any topic type as a pill
    case ActivityTopicTypesEnum.ASANA:
    case ActivityTopicTypesEnum.GITHUB:
    case ActivityTopicTypesEnum.JIRA:
    case ActivityTopicTypesEnum.TRELLO:
    case ActivityTopicTypesEnum.LINEAR:
    case ActivityTopicTypesEnum.MONDAY:
    case ActivityTopicTypesEnum.WEB:
      return (
        <ActivityTopicPillContainer
          conversationId={conversationId}
          topic={topic}
          shouldRemoveLeftMargin={options && options.shouldRemoveLeftMargin}
        />
      );
    default:
      return (
        <Trans id="activity-basic-topic">
          The {renderTopicTypeName(topic, options?.topicIntegration)} link &quot;{topic.name}&quot;
        </Trans>
      );
  }
}

/*
 * Source.
 */

/* Check if the source of an activity is a teammate, and form the appropriate display name. */
const TeammateActivitySource: FC<TeammateActivitySourceProps> = (props) => {
  // Pass activity source as well to ensure that source teammate belongs to an ActivitySourceTeammateModel.
  const {userId, sourceTeammate} = props;

  return renderTeammateNameOrYou(userId, sourceTeammate);
};

/* If the source teammate is out of office, render the display name with additional text about the out of office. */
const OutOfOfficeActivitySource: FC<OutOfOfficeActivitySourceProps> = (props) => {
  const {userId, sourceTeammate} = props;

  const sourceName = renderTeammateNameOrYou(props.userId, sourceTeammate);

  const targetName =
    userId === sourceTeammate.id ? (
      <Trans id="activity-unassigned-teammate-ooo-target-you">{sourceName} are</Trans>
    ) : (
      <Trans id="activity-unassigned-teammate-ooo-target-they">{sourceName} is</Trans>
    );

  return <Trans id="activity-unassigned-teammate-ooo-source">because {targetName} OOO</Trans>;
};

/* Render the name of a rule activity source with a link attached. */
const RuleActivitySource: FC<RuleActivitySourceProps> = (props) => {
  const {activitySource, timelineActivityAction} = props;

  const onClickTrackingCallback = () => {
    trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
      action: timelineActivityAction,
      rule_id: activitySource.ruleId,
      rule_bundle_id: activitySource.ruleBundleId,
      rule_bundle_type: activitySource.ruleBundleType,
    });
  };

  return (
    <StyledRulePillWrapperDiv>
      <ActivityRulePillContainer
        ruleId={activitySource.ruleId}
        ruleBundleId={activitySource.ruleBundleId}
        ruleBundleType={activitySource.ruleBundleType}
        ruleName={activitySource.ruleName}
        ruleNamespace={activitySource.ruleNamespace}
        onClickTrackingCallback={onClickTrackingCallback}
      />
    </StyledRulePillWrapperDiv>
  );
};

const ApiActivitySource: FC<ApiActivitySourceProps> = (props) => {
  const {activitySource} = props;

  if (
    activitySource.type !== ActivitySourceTypesEnum.PUBLIC_API ||
    activitySource.visualType !== ActivitySourceVisualTypesEnum.OTHER
  ) {
    return null;
  }

  return (
    <Trans id="activity-basic-tag-api">
      the <strong>API</strong>
    </Trans>
  );
};

// NOTE: This is being used as a fallback. For the new activities design, using
// the above activity source components to handle the new text arrangements.
const ActivitySource: FC<BasicViewerProps> = (props) => {
  const {source} = props.activity;
  switch (source.visualType) {
    case ActivitySourceVisualTypesEnum.TEAMMATE:
      return renderSourceTeammate(props, source);

    case ActivitySourceVisualTypesEnum.RULE:
      return renderSourceRule(source);

    case ActivitySourceVisualTypesEnum.CONTACT:
      return renderSourceContact(source);

    default:
      return renderSourceOther(source);
  }
};

function renderSourceTeammate(props: BasicViewerProps, source: ActivitySourceTeammateModel) {
  // Make sure we have a source teammate.
  const {sourceTeammate} = props;
  if (!sourceTeammate) {
    return null;
  }

  // If the teammate was unavailable, we need a different message.
  if (source.wasUnavailable) {
    return (
      <Trans id="activity-source-teammate-ooo">
        by {buildTeammateDisplayName(sourceTeammate)} (out of office)
      </Trans>
    );
  }

  if (source.wasDeleted) {
    return (
      <Trans id="activity-source-teammate-deleted">
        because {buildTeammateDisplayName(sourceTeammate)} is no longer an active Front user
      </Trans>
    );
  }

  // Otherwise, just use the teammate name.
  return <Trans id="activity-source-teammate">by {buildTeammateDisplayName(sourceTeammate)}</Trans>;
}

function renderSourceRule(source: ActivitySourceRuleModel) {
  return <Trans id="activity-source-rule">by a rule – &quot;{source.ruleName}&quot;</Trans>;
}

function renderSourceContact(source: ActivitySourceContactModel) {
  return <Trans id="activity-source-contact">by {source.name}</Trans>;
}

function renderSourceOther(source: ActivitySourceOtherModel) {
  switch (source.type) {
    case ActivitySourceTypesEnum.RULE:
      return <Trans id="activity-source-rulenoname">by a rule</Trans>;

    case ActivitySourceTypesEnum.REMINDER:
      return <Trans id="activity-source-reminder">by a reminder</Trans>;

    case ActivitySourceTypesEnum.INACTIVE:
      return <Trans id="activity-source-inactive">because of inactivity</Trans>;

    case ActivitySourceTypesEnum.PUBLIC_API:
      return <Trans id="activity-source-api">from the API</Trans>;

    case ActivitySourceTypesEnum.SPAM_FILTER:
      return <Trans id="activity-source-spam">by spam filtering</Trans>;

    case ActivitySourceTypesEnum.GMAIL:
      return <Trans id="activity-source-gmail">in the Gmail inbox</Trans>;

    case ActivitySourceTypesEnum.OFFICE365:
      return <Trans id="activity-source-office365">in the Office 365 inbox</Trans>;

    case ActivitySourceTypesEnum.INTERCOM:
      return <Trans id="activity-source-intercom">in Intercom</Trans>;

    case ActivitySourceTypesEnum.GITHUB:
      return <Trans id="activity-source-github">in Github</Trans>;

    case ActivitySourceTypesEnum.SLACK:
      return <Trans id="activity-source-slack">in Slack</Trans>;

    case ActivitySourceTypesEnum.CONVERSATION_SIZE:
      return <Trans id="activity-source-conversationsize">due to its size</Trans>;

    case ActivitySourceTypesEnum.DATA_LEAK:
      return (
        <Trans id="activity-source-dataleak">
          for privacy reasons.{' '}
          <InlineLinkButton onClick={onDataLeakLearnMoreClick}>Learn more</InlineLinkButton>.
        </Trans>
      );

    case ActivitySourceTypesEnum.COMPANY:
    case ActivitySourceTypesEnum.SENDING:
      return null;

    default:
      return <Trans id="activity-source-name">by {source.name}</Trans>;
  }
}

/*
 * Link.
 */

const ActivityConversationLink: FC<BasicViewerProps> = (props) => {
  const {activity, children} = props;
  const {navigateToLink, maybeRenderLinkError} = useLinkNavigation();

  function maybeRenderLink() {
    if (activity.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN) {
      const targetConversationPublicId = buildPublicId('conversation', activity.targetConversationId);

      const onLinkClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
          action: TimelineActivityActionsEnum.CLICK_SPLIT_CONVERSATION_LINK_TO_NEW,
          conversation_id: activity.targetConversationId,
        });

        navigateToLink(targetConversationPublicId);
      };

      return <InlineLinkButton onClick={onLinkClick}>{children}</InlineLinkButton>;
    }

    if (activity.basicVisualType === BasicVisualTypesEnum.SPLIT_TARGET) {
      const originConversationPublicId = buildPublicId('conversation', activity.originConversationId);

      const onLinkClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.TIMELINE_ACTIVITY_ACTION, {
          action: TimelineActivityActionsEnum.CLICK_SPLIT_CONVERSATION_LINK_TO_ORIGINAL,
          conversation_id: activity.originConversationId,
        });

        navigateToLink(originConversationPublicId);
      };

      return <InlineLinkButton onClick={onLinkClick}>{children}</InlineLinkButton>;
    }

    return null;
  }

  return (
    <>
      {maybeRenderLink()}
      {maybeRenderLinkError()}
    </>
  );
};

function onDataLeakLearnMoreClick() {
  openInBrowser(helpCenterDataLeakUrl);
}

/*
 * Helpers.
 */

function renderTopicTypeName(topic: TopicModel, topicIntegration?: IntegrationModel) {
  // If the type is web, we need to translate it.
  if (topic.type === ActivityTopicTypesEnum.WEB) {
    return <Trans id="activity-basic-topic-web">Web</Trans>;
  }
  return topicIntegration?.topicType?.resourceName ?? topicIntegration?.name ?? topic.type;
}

/* Render the name of a teammate, or 'you' if this teammate is the current user.  */
function renderTeammateNameOrYou(userId: number, teammate: TeammateModel) {
  const displayName = buildTeammateDisplayName(teammate);

  if (userId !== teammate.id) {
    return <>{displayName}</>;
  }

  return <Trans id="activity-basic-teammate-you">you</Trans>;
}

function renderInboxName(originalTeamInboxes: ReadonlyArray<InboxModel>) {
  if (!originalTeamInboxes.length) {
    return undefined;
  }

  if (originalTeamInboxes.length === 1) {
    return originalTeamInboxes[0].name;
  }

  return <Trans id="activity-basic-inbox-multiple">{originalTeamInboxes.length} inboxes</Trans>;
}

/* Render a tag item with its icon, and allow it to be clickable to take the user to their settings. */
function renderTag(props: BasicViewerProps, tag: TagModel) {
  return <ActivityTagPillContainer tag={tag} teammate={props.user} panelId={props.panelId} />;
}

/* Determine whether to a tag, untag, or gear icon (for rule or API). */
function computeTagOrTopicActivityIconName(activity: BasicActivityModel): IconName {
  if (
    activity.source.type === ActivitySourceTypesEnum.RULE &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.RULE
  ) {
    return 'gearActivity';
  }

  if (
    activity.source.type === ActivitySourceTypesEnum.PUBLIC_API &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER
  ) {
    return 'gearActivity';
  }

  switch (activity.type) {
    case BasicActivityTypesEnum.TAG:
      return 'tagActivity';
    case BasicActivityTypesEnum.UNTAG:
      return 'untagActivity';
    case BasicActivityTypesEnum.TOPIC_ADDED:
      return 'integrationActivity';
    case BasicActivityTypesEnum.TOPIC_REMOVED:
      return 'removeIntegrationActivity';
    default:
      return 'gearActivity';
  }
}
